<template>
  <div>
    <SlidingPanel
    ref="sliding-panel"
    test-id="user-panel"
    @slidingPanelClickOutside="confirmHide"    
  >
    <HdCircleLoader
      v-if="uc.isLoading"
    />
    <!-- navigation -->
    <template #header>
      <v-container fluid>
        <v-row class="mb-1">
          <span
            id="title"
            class="text-h5"
            data-testid="user-panel-title"
          > {{ formTitle }}</span>   
          <v-spacer></v-spacer>
          <HdIconButton 
            v-if="companyId === 1 && $accesscontrol().isGlobalAdmin"
            :icon="isImpersonateAccount ? 'fas fa-ghost' : 'far fa-ghost'"
            round
            :top="false"
            :tooltip="isImpersonateAccount ? $t('RemoveMyImpersonateAccount') : $t('AddAsMyImpersonateAccount')"
            @click="onToggleImpersonateAccount"
          />
        </v-row>
        <v-row>
          <v-btn
            v-show="!isIAnHiddenCompany(companyId)"
            color="secondarygreen3"        
            depressed
            small
            data-testid="user-panel-nav-info-button"
            @click="onNavigationButtonClick('title')"
          >
            info
          </v-btn>
          <v-btn
            v-show="notAnHiddenCompany"
            color="secondarygreen3"        
            class="mx-1"
            depressed
            small
            data-test="properties-button"
            data-testid="user-panel-nav-properties-button"
            @click="onNavigationButtonClick('properties')"
          >
            {{ $t('Properties') }}
          </v-btn>
          <v-btn
            v-show="notAnHiddenCompany && !useNewLicenseManager"
            color="secondarygreen3"
            class="mx-1"
            depressed
            small
            data-test="licenses-button"
            data-testid="user-panel-nav-licenses-button"
            @click="onNavigationButtonClick('licences')"
          >
            {{ $t('Licenses') }}
          </v-btn>
          <v-btn
            v-if="!isNew && notAnHiddenCompany && useNewLicenseManager"
            color="secondarygreen3"
            class="mx-1"
            depressed
            small
            data-test="manage-licenses-modal-button"
            data-testid="user-panel-manage-licenses-button"
            @click="manageLicensesModalOpen = true"
          >
            {{ $t('ManageLicenses') }}
          </v-btn>
          <v-btn
            v-if="!isNew && notAnHiddenCompany"
            color="secondarygreen3"
            class="mx-1 mb-1"
            depressed
            small
            data-testid="user-panel-nav-subdivisions-button"
            @click="onNavigationButtonClick('subdivisions')"
          >
            {{ $t('Subdivisions') }}
          </v-btn>

          <v-btn
            color="secondarygreen3"        
            class="mx-1"
            depressed
            small
            data-testid="user-panel-nav-entity-contacts-button"
            @click="onNavigationButtonClick('entitycontacts')"
          >
            {{ $t('Entitycontacts') }}
          </v-btn>
          <v-btn
            color="secondarygreen3"        
            class="mx-1"
            depressed
            small
            data-testid="user-panel-nav-entity-addresses-button"
            @click="onNavigationButtonClick('entityaddresses')"
          >
            {{ $t('Entityaddresses') }}
          </v-btn>
        </v-row>
      </v-container>
    </template>
    <!-- navigation end -->

    <!-- form -->
    <HdCreateEditUserForm 
      v-if="!uc.isLoading" 
      ref="form"
      :user="user"
      :password-policy-id="passwordPolicyId"
      :allow-empty-password="!isNew"
      :can-send-set-password-email="isNew && notAnHiddenCompany"
      :can-resend-set-password-email="!isNew && notAnHiddenCompany"
      :resending-set-password-email="resendingSetPasswordEmail"
      :show-db-id="!isNew"
      :show-avatar="!isNew"
      @resend-set-password-email="onResendSetPasswordEmail"
    />
    <v-container
      v-if="!uc.isLoading" 
      fluid
    >
      <!-- user group -->
      <HdGroupSelector 
        v-show="notAnHiddenCompany"
        ref="group"
        :selected-group="user.groupId"
        test-id="user-group"
      />
      <!-- properties -->
      <PropertyList
        v-show="notAnHiddenCompany"
        id="properties"
        ref="propform"
        :selected-properties="user.properties"
      />
        
      <!-- licenses -->
      <ApplicationToggleList 
        v-show="notAnHiddenCompany && !useNewLicenseManager"
        id="licences"
        ref="appform"
        :selected-applications="user.applications"
      />
      <UserSubdivisionList
        v-show="!isNew && notAnHiddenCompany"
        id="subdivisions"
        ref="subdivisionlist"
        :user="user"
      />
      <UserShortcutList
        v-show="!isNew && $accesscontrol().isPartnerAdmin && hasDrift && notAnHiddenCompany"
        id="shortcuts"
        ref="shortcutlist"
        :user="user"
        :company-id="companyId"
      />
      <div
        v-show="notAnHiddenCompany"
        v-select-test-parts
        data-testid="user-default-email-select"
      >
        <v-select
          v-model="user.defaultMail"
          :items="mailTypes"
          item-text="text"
          item-value="value"
          :label="$t('DefaultEmail')"
          :hint="$t('DefaultEmail')"
          persistent-hint
          single-line
        >
          <template #item="{ item }">
            <v-list-item-content :data-testid="testIdFor('user-default-email-option', item.value)">
              <v-list-item-title>{{ item.text }}</v-list-item-title>
            </v-list-item-content>
          </template>
        </v-select>
      </div>
      <MailSignatureEditor
        v-if="user && user.userDbId && notAnHiddenCompany"
        ref="signatureEditor"
        type="UserDbId"
        :external-id="user.userDbId"
      />
      <!--Entity contacts-->
      <HdEntityContactFields
        id="entitycontacts"
        ref="entityContactFields"
        :entity-contact="user.entityContact"
        @update="updateEntityContact"
      />
      <!--Entity addresses-->
      <HdEntityAddressFields
        id="entityaddresses"
        ref="entityAddressFields"
        :entity-address="user.entityAddress"
        entity-type="user"
        :entity-id="user.userDbId"
        @updateAddress="updateEntityAddress"
      />
    </v-container>
    <template #footer>
      <v-btn

        v-show="!uc.isLoading"
        ref="saveButton"
        color="success"
        :loading="user.isLoading"
        data-testid="user-panel-save-button"
        @click="onSubmit"
      >
        {{ $t('Save') }}
      </v-btn>
      <v-btn            
        v-show="!uc.isLoading"
        text
        data-testid="user-panel-close-button"
        @click="confirmHide"
      >
        {{ $t('Close') }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        v-if="$accesscontrol().isSuperAdmin && !isNew"
        text
        color="error"
        data-testid="user-panel-move-button"
        @click="moveDialogOpen = true"
      >
        {{ $t('Move') }}
      </v-btn>
      <v-btn
        v-show="!uc.isLoading"
        v-if="user.has2fa"
        color="error"
        text
        :loading="removing2fa"
        data-testid="user-panel-reset-2fa-button"
        @click.prevent="onRemove2Fa"
      >
        {{ $t('Reset2FA') }}
      </v-btn>
    </template>
    <HdCustomizableConfirmModal 
      ref="customconfirm"
    />
    <ChangeUserCompanyDialog 
      v-model="moveDialogOpen"
      :user="user"
      @usermoved="hide()"
    />
  </SlidingPanel>
  <v-dialog
    v-model="manageLicensesModalOpen"
    max-width="90%"
    width="900"
    :persistent="false"
    @click:outside="closeManageLicensesModal"
  >
    <div
      class="manage-licenses-dialog-wrapper"
      @click.self="closeManageLicensesModal"
    >
      <v-card
        class="manage-licenses-dialog-card"
        @click.stop
      >
      <v-card-title class="manage-licenses-dialog-title">
        <span>{{ $t('ManageLicenses') }}</span>
        <v-spacer />
        <v-btn
          icon
          aria-label="Close"
          @click.stop="closeManageLicensesModal"
        >
          <v-icon>fas fa-times</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="manage-licenses-dialog-content">
        <manage-licenses-modal
          v-if="manageLicensesModalOpen"
          id="manage-licenses-modal"
          open="true"
          :token="authToken || ''"
          :user-db-id="String(user.userDbId || '')"
          :api-base-url="licensesApiUrl"
          :translations-base-url="translationsApiUrl"
          :language="$i18n.locale"
          :dark-theme="$vuetify.theme.dark ? 'true' : 'false'"
          @manage-licenses-close="closeManageLicensesModal"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="closeManageLicensesModal"
        >
          {{ $t('Close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
    </div>
  </v-dialog>
  </div>
</template>

<script>
import { User } from '../../../model/user/User'
import { UserCollection } from '../../../model/user/UserCollection'
import SlidingPanel from '../../SlidingPanel'
import { mapGetters } from 'vuex'
import HdCreateEditUserForm from './HdCreateEditUserForm'
import PropertyList from '../PropertyList'
import ApplicationToggleList from '../ApplicationToggleList'
import HdGroupSelector from '../../form/field/HdGroupSelector'
import UserSubdivisionList from '../../list/UserSubdivisionList'
import ChangeUserCompanyDialog from './ChangeUserCompanyDialog.vue'
import UserShortcutList from '../../list/UserShortcutList.vue'
import { Company } from '../../../model/company/Company'
import MailSignatureEditor from '../subdivision/MailSignatureEditor.vue'
import HdEntityContactFields from '../field/HdEntityContactFields'
import HdEntityAddressFields from '../field/HdEntityAddressFields'
import { isIAnHiddenCompany, isAlreadyExistsError } from '../../../util/tools'
import { testIdFor, selectTestParts } from '../../../util/testId'
import { LICENSES_API_URL, TRANSLATIONS_API_URL } from '../../../util/URL'

export default {
  name: 'CreateEditUserPanel',
  directives: { selectTestParts },
  components: {
    SlidingPanel,
    HdCreateEditUserForm,
    ApplicationToggleList,
    HdGroupSelector,
    UserSubdivisionList,
    PropertyList,
    UserShortcutList,
    ChangeUserCompanyDialog,
    MailSignatureEditor,
    HdEntityContactFields,
    HdEntityAddressFields,
  },
  props: {
    standAlone: {

      type: Boolean,
      required: false,
      default() {
        return false
      }
    },
    company: {
      type: Company,
      required: false,
      default() {
        return null
      }
    }
  },
  data() {
    return {
      notAnHiddenCompany: !isIAnHiddenCompany(this.companyId),
      formTitle: '',
      dialogOpen: false,
      user: new User({}),
      companyId: 0,
      isNew: false,
      uc: new UserCollection({}),
      removing2fa: false,
      resendingSetPasswordEmail: false,
      passwordPolicyId: -1,
      mailTypes: [
        { text: '', value: 'None' },
        { text: this.$t('CompanyEmail'), value: 'CompanyMail' },
        { text: this.$t('UserEmail'), value: 'UserMail' },
      ],
      moveDialogOpen: false,
      manageLicensesModalOpen: false,
      manageLicensesModalEscapeBound: null,
      licensesApiUrl: LICENSES_API_URL,
      translationsApiUrl: TRANSLATIONS_API_URL,
    }
  },
  computed: {
    ...mapGetters({
      getImpersonateAccount: 'user/getImpersonateAccount'
    }),
    authToken() {
      const token = this.$store.state.auth && this.$store.state.auth.token
      return token || (typeof localStorage !== 'undefined' && localStorage.getItem('user-auth-token'))
    },
    isImpersonateAccount() {
      return this.getImpersonateAccount?.userDbId === this.user.userDbId
    },
    hasDrift() {
      return this.company && this.company.properties.findIndex(prop => prop.propertyId === 11 && prop.propertyValue) > -1
    },
    useNewLicenseManager() {
      const prop = this.company && (this.company.properties || []).find(
        p => (p.propertyName === 'LicenseServiceOnboard' || (p.propertyName && p.propertyName.endsWith('LicenseServiceOnboard')))
      )
      return !!(prop && (prop.propertyValue === 'true' || prop.propertyValue === 'True'))
    },
  },
  beforeDestroy() {
    this.removeManageLicensesModalEscape()
  },
  watch: {
    manageLicensesModalOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => this.addManageLicensesModalEscape())
      } else {
        this.removeManageLicensesModalEscape()
      }
    },
  },
  methods: {
    testIdFor,
    /**
     * Display user edit / create from
     *
     * @param {number} userDbId selected user 
     * @param {number} companyId company where user is added / edited
     * @param {number} passwordPolicyId company's password policy 
     */
    show(userDbId, companyId, passwordPolicyId) {
      if(userDbId && userDbId > 0) {   
        this.isNew = false
      
        this.uc.fetchById(userDbId)
        .then((res) => {
          this.user = res
          return this.user.is2FaEnabled()   
        })
        .catch((ex) => {
          this.$alert({ message: this.$handleError(ex), type: 'error'})
          this.$refs['sliding-panel'].close()
          return false
        })
      } else {
        this.isNew = true
        this.user = new User({
          active: true,
          entityContact: {
            phone: '',
            commercialPhone: '',
            email: '',
            invoicingEmail: '',
            trainingEmail: ''
          }
        })
        this.$refs.form.validate()
      }
      this.companyId = companyId   
      this.passwordPolicyId = passwordPolicyId           
      this.formTitle = this.isNew ? this.$t('AddNewUser') : this.$t('EditUser')
      this.$refs['sliding-panel'].open()
    },

    confirmHide() {
      //this.user.features = null
      this.user.groupId = this.$refs.group.getValue()
      //this.user.endpointId = null
      this.user.password = this.$refs.form.getValues().password || ''
      this.user.properties = this.$refs.propform.getValues()
      if (!this.useNewLicenseManager) {
        this.user.applications = this.$refs.appform.getValues()
      }
      const hasChanges = this.user.hasChanges() || (this.$refs.signatureEditor && this.$refs.signatureEditor.hasChanges())
      const notValid = !this.$refs.form.validate()
      
      if (hasChanges) {
        this.$refs.customconfirm.show({ 
          title: this.$t('Confirm'), 
          text: this.$t('DiscardUnsavedChanges'),
          buttons: [
            { id: 'discard', text: this.$t('Discard'), textOnly: true },
            { id: 'save', text: this.$t('Save'), textOnly: true, disabled: notValid },
            { id: 'stay', text: this.$t('KeepEditing'), color: 'primary' },
          ]
        })
        .then((id) => {
          if (id === 'discard') {
            this.hide()
          } else if (id === 'save') {
            this.onSubmit()
          }
        })
      } else {
        this.hide()
      }
    },
    /**
     * Hide this dialog
     * 
     * Used outside of this component
     */
    hide() {
      this.$refs.form.clear()
      this.user = new User({active: true})
      this.$refs['sliding-panel'].close()
    },
    /**
     * Gets user data from form and emits an event where
     * user object is passed forward       
     */
    onSubmit() {
      this.$refs.form.clearUserIdError?.()
      if(!this.$refs.form.validate()) {
        return
      }

      let formValues = this.$refs.form.getValues()
      
      this.user.features = null
      this.user.groupId = this.$refs.group.getValue()
      this.user.endpointId = null
      this.user.password = formValues.password
      this.user.properties = this.$refs.propform.getValues()
      if (!this.useNewLicenseManager) {
        this.user.applications = this.$refs.appform.getValues()
      }
      this.user.entityContact = this.$refs.entityContactFields.getValues()     
      this.user.companyId = this.companyId
      const promise = this.$refs.signatureEditor ? this.$refs.signatureEditor.updateSignature() : Promise.resolve()
      promise
      .then(() => {
        this.$emit('userformsubmit', this.user, this.isNew, { sendSetPasswordEmail: formValues.sendSetPasswordEmail })
      })
      .then(() => {
        if(this.standAlone) {
          return this.user.update()
          .then(() => {
            this.hide()
          })
          .catch((ex) => {
            if (!this.showUserExistsError(ex)) {
              this.$alert({ type: 'error', message: this.$handleError(ex) })
            }
          })
        }
      })
    },

    /**
     * A 409 from the API means the user id is taken. Report it under the user id field the
     * way the client side validation errors are reported, and keep the panel open so the
     * address can be corrected, instead of throwing a modal over everything.
     *
     * @param {*} ex the error thrown by the api client
     * @returns {Boolean} true when the error was handled here
     */
    showUserExistsError(ex) {
      if (!isAlreadyExistsError(ex)) {
        return false
      }
      this.$refs.form?.setUserIdError(this.$t('UserIdInUse'))
      return true
    },

    onNavigationButtonClick(target) {
      let anchor = '#' + target
      this.$vuetify.goTo(anchor, {
        container: '.sliding-panel.expanded .sliding-panel-content'
      })
    },
    onRemove2Fa() {
      this.$confirm({
        color: 'error', 
        title: this.$t('Reset2FA'), 
        text: this.$t('Reset2FA'),
        confirmButtonText: this.$t('Remove'),        
      })
      .then(() => {
        this.removing2fa = true
        return this.user.remove2fa()
      })
      .then(()=>{
          this.$alert({ message: this.$t('Deleted'), type: 'success'})

      })
      .catch((ex) => {          
        if(ex !== 'cancel') {
          // api error  
          this.$alert({type: 'warning', message: this.$handleError(ex) })
        }
      })
      .finally(() => {
        this.removing2fa = false
      })

    },

    /**
     * Mails the user a fresh link to set their own password - used when their first
     * link expired before they got round to setting one. Confirmed first because it
     * sends real mail, and any earlier link stops working. A password the user has
     * already set keeps working; the link only ever adds a way to replace it.
     */
    onResendSetPasswordEmail() {
      this.$confirm({
        color: 'primary',
        title: this.$t('ResendSetPasswordEmail'),
        text: `${this.user.userId}\n\n${this.$t('ResendSetPasswordEmailConfirm')}`,
        confirmButtonText: this.$t('Resend'),
      })
      .then(() => {
        this.resendingSetPasswordEmail = true
        return this.user.resendSetPasswordEmail()
      })
      .then(() => {
        this.$toast({ type: 'success', message: `${this.$t('EMailSent')}: ${this.user.userId}` })
      })
      .catch((ex) => {
        if (ex !== 'cancel') {
          this.$alert({ type: 'warning', message: this.$handleError(ex) })
        }
      })
      .finally(() => {
        this.resendingSetPasswordEmail = false
      })
    },

    onToggleImpersonateAccount() {
      if(this.getImpersonateAccount?.userDbId === this.user.userDbId) {
        this.$store.dispatch('user/setImpersonateAccount', null)
      }else {
        const storeObject = {
          userName: this.user.userName,
          userDbId: this.user.userDbId,
          userId: this.user.userId,
          companyId: this.companyId,
          passwordPolicyId: this.passwordPolicyId
        }
        this.$store.dispatch('user/setImpersonateAccount', storeObject)
      }
    },

    /**
     * Display subdivision selection
     */
    showSubdivisionSelect() {
      //
      this.isNew = false
      return this.$refs.subdivisionlist.onConnectionDialog()
    },
    updateEntityContact(newEntityContact) {
      this.user.entityContact = newEntityContact
    },
    updateEntityAddress(newEntityAddress) {
      this.user.entityAddress = newEntityAddress
    },

    closeManageLicensesModal() {
      this.removeManageLicensesModalEscape()
      this.manageLicensesModalOpen = false
    },
    addManageLicensesModalEscape() {
      if (this.manageLicensesModalEscapeBound) return
      this.manageLicensesModalEscapeBound = (e) => {
        if (e.key === 'Escape') this.closeManageLicensesModal()
      }
      document.addEventListener('keydown', this.manageLicensesModalEscapeBound)
    },
    removeManageLicensesModalEscape() {
      if (!this.manageLicensesModalEscapeBound) return
      document.removeEventListener('keydown', this.manageLicensesModalEscapeBound)
      this.manageLicensesModalEscapeBound = null
    },
    isIAnHiddenCompany(companyId) {
      return isIAnHiddenCompany(companyId)
    },


  },
}
</script>
<style scoped>
.manage-licenses-dialog-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
}
.manage-licenses-dialog-card {
  position: relative;
  z-index: 1;
  max-width: 900px;
  width: 90%;
}
.manage-licenses-dialog-title {
  flex-shrink: 0;
  z-index: 2;
}
.manage-licenses-dialog-content {
  position: relative;
  z-index: 1;
  overflow: visible;
}
</style>
